import {
  type ColumnDef,
  type ColumnFiltersState,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  type RowData,
  type SortingState,
  type Table,
  useReactTable,
} from '@tanstack/react-table';
import { useState } from 'react';

export { SortIndicator } from '../components/sortIndicator';

type UseTradingTableOptions<T extends RowData> = {
  data: T[];
  columns: ColumnDef<T, any>[];
  initialSorting?: SortingState;
  enableFiltering?: boolean;
};

type UseTradingTableReturn<T extends RowData> = {
  table: Table<T>;
  sorting: SortingState;
  setSorting: React.Dispatch<React.SetStateAction<SortingState>>;
  columnFilters: ColumnFiltersState;
  setColumnFilters: React.Dispatch<React.SetStateAction<ColumnFiltersState>>;
};

export function useTradingTable<T extends RowData>({
  data,
  columns,
  initialSorting = [],
  enableFiltering = false,
}: UseTradingTableOptions<T>): UseTradingTableReturn<T> {
  const [sorting, setSorting] = useState<SortingState>(initialSorting);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);

  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
      ...(enableFiltering ? { columnFilters } : {}),
    },
    onSortingChange: setSorting,
    ...(enableFiltering ? { onColumnFiltersChange: setColumnFilters } : {}),
    autoResetAll: false,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    ...(enableFiltering ? { getFilteredRowModel: getFilteredRowModel() } : {}),
  });

  return { table, sorting, setSorting, columnFilters, setColumnFilters };
}

export type {
  ColumnDef,
  ColumnFiltersState,
  SortingState,
} from '@tanstack/react-table';
